import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

// -----------------------------------------------------------------------------
// Button.
//
// shadcn's primitive, retuned for this panel: 4px corners, 14px text, and a
// two-tone focus ring (violet core, lavender halo) that is actually visible -
// #C8A4F6 alone is 1.9:1 on white and fails WCAG 2.2's 3:1 focus minimum.
//
// `loading` is built in rather than left to call sites. Every destructive action
// in this app has to be disabled while in flight, and a prop is far harder to
// forget than a convention.
// -----------------------------------------------------------------------------

const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium',
    // `colors, box-shadow, transform` rather than `all`: transitioning `all`
    // also animates width and padding, so a button whose label changes (Sign in
    // → Signing in…) visibly stretches instead of just swapping text.
    'transition-[colors,box-shadow,transform] duration-150 ease-out',
    // A 1px press. Enough to register as a physical acknowledgement, small
    // enough that it never reads as the layout moving.
    'active:translate-y-px',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50 disabled:active:translate-y-0',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ),
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-card hover:bg-brand-deep hover:shadow-brand',
        destructive:
          'bg-destructive text-destructive-foreground shadow-card hover:bg-destructive/90',
        outline:
          'border border-input bg-background text-foreground shadow-card hover:border-lavender hover:bg-accent hover:text-accent-foreground',
        secondary:
          'border border-border bg-secondary text-secondary-foreground hover:bg-accent hover:text-accent-foreground',
        ghost: 'text-secondary-foreground hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        success: 'bg-success text-white shadow-card hover:bg-success/90',
      },
      size: {
        default: 'h-9 px-3.5 text-base [&_svg]:size-4',
        sm: 'h-8 px-3 text-sm [&_svg]:size-3.5',
        lg: 'h-10 px-5 text-base [&_svg]:size-4',
        icon: 'h-9 w-9 [&_svg]:size-4',
        'icon-sm': 'h-8 w-8 [&_svg]:size-3.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    // `asChild` renders a Link or an anchor. A spinner cannot be injected into an
    // arbitrary child without breaking Slot's single-child rule, so loading is
    // only honoured on a real <button>.
    if (asChild) {
      return (
        <Slot className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props}>
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? <Loader2 className="animate-spin" aria-hidden /> : null}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
